import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Button,
  FormField,
  Input,
  Textarea,
  Switch,
  SideDrawer,
} from '@/components';
import {
  useCreateQuotationProduct,
  useUpdateQuotationProduct,
  useQuotationProduct,
} from '../hooks';
import {
  FIELD_LABEL_CLASS,
  resolveQuotationMediaUrl,
  normalizeTaxRate,
  TAX_RATE_PRESETS,
  QUOTATION_PRODUCT_IMAGE_ACCEPT,
  QUOTATION_PRODUCT_IMAGE_LABEL,
  validateQuotationProductImage,
} from '../utils';
import { parseApiError } from '@/common/utils';
import { QuotationActivityTimeline } from './QuotationActivityTimeline';

const EMPTY_FORM = {
  name: '',
  description: '',
  price: '',
  moq: '1',
  tax_rate: '0',
  image_url: '',
  sku: '',
  is_available: true,
};

function isExternalWebUrl(url) {
  if (typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  // If it's a relative path or points to internal Django media storage, it is NOT an external URL
  if (
    trimmed.startsWith('/') ||
    trimmed.startsWith('media/') ||
    trimmed.includes('/media/') ||
    trimmed.includes('media/quotation_products')
  ) {
    return false;
  }
  return trimmed.startsWith('http://') || trimmed.startsWith('https://');
}

/**
 * QuotationProductDrawer - Slide-over drawer for creating or editing a quotation product.
 * Replaces separate full-page routes with an in-place side panel workflow.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the drawer is visible
 * @param {() => void} props.onClose - Close callback
 * @param {string|number} props.companyId - Active company ID
 * @param {string|number} props.quotationId - Active quotation ID
 * @param {Object|null} [props.product] - Product to edit, or null/undefined for create
 * @param {string|number|null} [props.productId] - Optional product ID to load details from API
 * @param {(product: Object) => void} [props.onSuccess] - Callback when product is saved
 */
export function QuotationProductDrawer({
  isOpen,
  onClose,
  companyId,
  quotationId,
  product = null,
  productId = null,
  onSuccess,
}) {
  const queryClient = useQueryClient();
  const targetProductId = product?.id || productId;
  const isEditing = Boolean(targetProductId);
  const fileInputRef = useRef(null);

  // Fetch fresh product details from API when editing
  const {
    data: fetchedProduct,
    isLoading: isFetchingProduct,
    isError: isProductError,
    error: productFetchError,
    refetch: refetchProduct,
  } = useQuotationProduct(
    { companyId, quotationId, productId: targetProductId },
    { enabled: isOpen && Boolean(targetProductId) },
  );

  const activeProduct = fetchedProduct || product;

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [imageSource, setImageSource] = useState('file'); // 'file' | 'url'
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [imageRemoved, setImageRemoved] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details' | 'history'

  const createMutation = useCreateQuotationProduct();
  const updateMutation = useUpdateQuotationProduct();
  const isPending = createMutation.isPending || updateMutation.isPending;

  // Initialize or reset form when product or open state changes
  useEffect(() => {
    if (isOpen) {
      setErrors({});
      setImageFile(null);
      setImageRemoved(false);
      setIsDragging(false);
      setActiveTab('details');

      if (activeProduct) {
        const rawImageUrl =
          activeProduct.image ||
          activeProduct.image_url ||
          activeProduct.effective_image_url ||
          '';
        const hasExternalUrl = isExternalWebUrl(rawImageUrl);

        const loaded = {
          name: activeProduct.name || '',
          description: activeProduct.description || '',
          price: activeProduct.price != null ? String(activeProduct.price) : '',
          moq: activeProduct.moq != null ? String(activeProduct.moq) : '1',
          tax_rate:
            activeProduct.tax_rate != null && activeProduct.tax_rate !== ''
              ? String(activeProduct.tax_rate)
              : '0',
          // Only populate image_url if it is an external web link! Never populate with backend /media/... endpoint
          image_url: hasExternalUrl ? rawImageUrl : '',
          sku: activeProduct.sku || '',
          is_available: activeProduct.is_available !== false,
        };
        setForm(loaded);
        setImageSource(hasExternalUrl ? 'url' : 'file');
        setImagePreview(
          rawImageUrl ? resolveQuotationMediaUrl(rawImageUrl) : '',
        );
      } else {
        setForm(EMPTY_FORM);
        setImageSource('file');
        setImagePreview('');
      }
    }
  }, [isOpen, activeProduct]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field] || (field === 'image_url' && errors.image)) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        delete next.image;
        return next;
      });
    }
  };

  const processSelectedFile = (file) => {
    if (!file) return;
    const check = validateQuotationProductImage(file);
    if (!check.ok) {
      toast.error(check.reason);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setImageFile(file);
    setImageRemoved(false);
    setField('image_url', ''); // Automatically clear URL when file is uploaded
    setImagePreview(URL.createObjectURL(file));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processSelectedFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) {
      processSelectedFile(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    setImageRemoved(true);
    setField('image_url', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    if (isPending) return;
    onClose();
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Product name is required.';
    if (!form.price || Number.isNaN(parseFloat(form.price))) {
      next.price = 'A valid price is required.';
    } else if (parseFloat(form.price) < 0) {
      next.price = 'Price cannot be negative.';
    }
    const moq = parseInt(form.moq, 10);
    if (!moq || moq < 1) next.moq = 'MOQ must be at least 1.';
    if (form.tax_rate !== '') {
      const n = Number(form.tax_rate);
      if (Number.isNaN(n) || n < 0 || n > 100) {
        next.tax_rate = 'Enter a tax rate between 0 and 100.';
      }
    }

    // Validation: either uploaded image or image URL, not both at the same time
    if (imageFile && form.image_url.trim()) {
      next.image =
        'Please provide either an uploaded image or an image URL, not both.';
    }

    if (imageSource === 'url' && form.image_url.trim()) {
      const url = form.image_url.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        next.image_url =
          'Image URL must be a valid link starting with http:// or https://';
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const tax_rate = normalizeTaxRate(form.tax_rate);
    if (tax_rate == null) {
      setErrors((p) => ({ ...p, tax_rate: 'Enter a valid tax rate.' }));
      return;
    }

    let finalImageUrl = undefined;
    if (imageSource === 'url') {
      finalImageUrl = form.image_url.trim() || undefined;
    } else if (imageRemoved) {
      finalImageUrl = ''; // Explicitly cleared
    } else if (!imageFile && activeProduct?.image_url) {
      finalImageUrl = activeProduct.image_url; // Preserve existing image
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      price: form.price,
      moq: parseInt(form.moq, 10) || 1,
      tax_rate,
      image_url: imageFile ? undefined : finalImageUrl,
      sku: form.sku.trim() || undefined,
      is_available: form.is_available,
    };

    if (isEditing && activeProduct?.order != null) {
      payload.order = activeProduct.order;
    }

    const onError = (err) => {
      const { message, fieldErrors } = parseApiError(err);
      if (fieldErrors && Object.keys(fieldErrors).length) {
        setErrors(fieldErrors);
      }
      toast.error(message || 'Failed to save product.');
    };

    const handleSuccessCallback = (data) => {
      queryClient.invalidateQueries({
        queryKey: ['quotation-products', companyId, quotationId],
      });
      queryClient.invalidateQueries({
        queryKey: ['quotation', companyId, quotationId],
      });
      if (targetProductId) {
        queryClient.invalidateQueries({
          queryKey: [
            'quotation-product',
            companyId,
            quotationId,
            targetProductId,
          ],
        });
      }
      toast.success(isEditing ? 'Product updated.' : 'Product added.');
      onSuccess?.(data);
      onClose();
    };

    if (isEditing) {
      updateMutation.mutate(
        {
          companyId,
          quotationId,
          productId: targetProductId,
          payload,
          imageFile: imageFile || undefined,
        },
        {
          onSuccess: handleSuccessCallback,
          onError,
        },
      );
    } else {
      createMutation.mutate(
        {
          companyId,
          quotationId,
          payload,
          imageFile: imageFile || undefined,
        },
        {
          onSuccess: handleSuccessCallback,
          onError,
        },
      );
    }
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={handleClose}
      width="lg"
      className="q-drawer sm:w-[65vw] sm:max-w-[70vw] lg:w-[45vw] lg:max-w-[640px]"
      title={isEditing ? 'Edit Product' : 'Create Product'}
      description={
        isEditing
          ? 'Update product details, pricing, and catalog availability.'
          : 'Add a new product to this quotation template.'
      }
      footer={
        <div className="flex items-center justify-end gap-3 w-full">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isPending}
            className="h-10 px-4 text-sm"
          >
            {activeTab === 'history' ? 'Close' : 'Cancel'}
          </Button>
          {activeTab === 'details' && (
            <Button
              type="submit"
              form="quotation-product-drawer-form"
              variant="primary"
              disabled={
                isPending ||
                (isEditing && isFetchingProduct) ||
                (isEditing && isProductError)
              }
              className="h-10 px-5 text-sm"
            >
              {isPending
                ? 'Saving…'
                : isEditing
                  ? 'Update Product'
                  : 'Add Product'}
            </Button>
          )}
        </div>
      }
    >
      {isEditing && (
        <div className="flex items-center gap-1.5 p-1 bg-[#F5F5F5] rounded-xl w-fit mb-4">
          <button
            type="button"
            onClick={() => setActiveTab('details')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeTab === 'details'
                ? 'bg-white text-[#50AB3D] shadow-sm'
                : 'text-[#868686] hover:text-[#1F2241]'
            }`}
          >
            Details
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('history')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeTab === 'history'
                ? 'bg-white text-[#50AB3D] shadow-sm'
                : 'text-[#868686] hover:text-[#1F2241]'
            }`}
          >
            History
          </button>
        </div>
      )}

      {isEditing && activeTab === 'history' ? (
        <QuotationActivityTimeline
          companyId={companyId}
          quotationId={quotationId}
          productId={targetProductId}
          title="Product History"
          compact
          className="border-0 shadow-none p-0 rounded-none"
          scrollClassName="max-h-[min(520px,60vh)]"
        />
      ) : isEditing && isProductError ? (
        <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
          <div className="w-12 h-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center font-bold text-lg">
            !
          </div>
          <p className="text-sm font-semibold text-[#1F2241]">
            Failed to load product details
          </p>
          <p className="text-xs text-[#868686] max-w-sm">
            {parseApiError(productFetchError).message ||
              'Unable to retrieve product information from server.'}
          </p>
          <Button
            type="button"
            variant="secondary"
            onClick={() => refetchProduct()}
            className="mt-2 text-xs h-8 px-4"
          >
            Retry
          </Button>
        </div>
      ) : isEditing && isFetchingProduct && !fetchedProduct ? (
        <div className="flex flex-col gap-6 py-4 animate-pulse">
          <div className="flex flex-col gap-4">
            <div className="h-4 w-32 bg-[#EBF4EA] rounded" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="h-10 bg-[#F5F5F5] rounded-[10px]" />
              <div className="h-10 bg-[#F5F5F5] rounded-[10px]" />
            </div>
            <div className="h-24 bg-[#F5F5F5] rounded-[10px]" />
          </div>
          <div className="flex flex-col gap-4">
            <div className="h-4 w-32 bg-[#EBF4EA] rounded" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="h-10 bg-[#F5F5F5] rounded-[10px]" />
              <div className="h-10 bg-[#F5F5F5] rounded-[10px]" />
            </div>
          </div>
          <div className="h-32 bg-[#F5F5F5] rounded-[12px]" />
        </div>
      ) : (
        <form
          id="quotation-product-drawer-form"
          onSubmit={handleSubmit}
          noValidate
          className="flex flex-col gap-6"
        >
          {/* ── Section: Product Details ── */}
          <div className="flex flex-col gap-4">
            <h4 className="text-xs font-bold uppercase tracking-wider text-[#50AB3D] border-b border-[#EBF4EA] pb-1.5">
              Product Details
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                label="Product Name"
                htmlFor="drawer-product-name"
                required
                error={errors.name}
                labelClassName={FIELD_LABEL_CLASS}
                className="sm:col-span-1"
              >
                <Input
                  id="drawer-product-name"
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="e.g. Classic T-Shirt"
                  maxLength={255}
                  error={Boolean(errors.name)}
                  autoFocus
                />
              </FormField>

              <FormField
                label="SKU"
                htmlFor="drawer-product-sku"
                labelClassName={FIELD_LABEL_CLASS}
                className="sm:col-span-1"
              >
                <Input
                  id="drawer-product-sku"
                  value={form.sku}
                  onChange={(e) => setField('sku', e.target.value)}
                  placeholder="SKU-001"
                />
              </FormField>

              <FormField
                label="Description"
                htmlFor="drawer-product-description"
                labelClassName={FIELD_LABEL_CLASS}
                className="sm:col-span-2"
              >
                <Textarea
                  id="drawer-product-description"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                  placeholder="Brief description of the product…"
                  className="min-h-[68px] resize-none"
                />
              </FormField>

              {/* Product Image */}
              <FormField
                label="Product Image"
                error={errors.image || errors.image_url}
                labelClassName={FIELD_LABEL_CLASS}
                className="sm:col-span-2"
              >
                {/* Mutually exclusive source selector */}
                <div className="flex items-center gap-1.5 p-1 bg-[#F5F5F5] rounded-xl w-fit mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      setImageSource('file');
                      setField('image_url', '');
                      if (imageFile) {
                        setImagePreview(URL.createObjectURL(imageFile));
                      } else if (product?.image_url && !imageRemoved) {
                        setImagePreview(
                          resolveQuotationMediaUrl(product.image_url),
                        );
                      } else {
                        setImagePreview('');
                      }
                      if (errors.image || errors.image_url) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.image;
                          delete next.image_url;
                          return next;
                        });
                      }
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      imageSource === 'file'
                        ? 'bg-white text-[#50AB3D] shadow-sm'
                        : 'text-[#868686] hover:text-[#1F2241]'
                    }`}
                  >
                    Upload Image
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setImageSource('url');
                      setImageFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                      setImagePreview(form.image_url.trim() || '');
                      if (errors.image || errors.image_url) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.image;
                          delete next.image_url;
                          return next;
                        });
                      }
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      imageSource === 'url'
                        ? 'bg-white text-[#50AB3D] shadow-sm'
                        : 'text-[#868686] hover:text-[#1F2241]'
                    }`}
                  >
                    Image URL
                  </button>
                </div>

                {imageSource === 'file' ? (
                  /* File Upload Dropzone */
                  <div className="flex flex-col gap-2">
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`w-full flex flex-col items-center justify-center rounded-xl border-2 border-dashed transition-all cursor-pointer group p-4 ${
                        isDragging
                          ? 'border-[#50AB3D] bg-[#F0FDF4] scale-[1.01]'
                          : 'border-[#EBF4EA] bg-[#FAFAFA] hover:border-[#50AB3D]/40'
                      }`}
                    >
                      {isDragging ? (
                        <div className="flex flex-col items-center justify-center pointer-events-none py-3">
                          <div className="w-10 h-10 rounded-full bg-[#EBF4EA] flex items-center justify-center text-[#50AB3D] mb-2 animate-bounce">
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                              />
                            </svg>
                          </div>
                          <p className="text-xs font-semibold text-[#50AB3D]">
                            Drop image here to upload
                          </p>
                          <p className="text-[10px] text-[#868686] mt-0.5">
                            {QUOTATION_PRODUCT_IMAGE_LABEL}
                          </p>
                        </div>
                      ) : imagePreview && !imageRemoved ? (
                        <div className="relative group/preview flex items-center justify-center">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="h-28 w-28 rounded-lg object-contain bg-white border border-[#EBF4EA] shadow-sm p-1"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveImage();
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600 transition-colors"
                            title="Remove image"
                            aria-label="Remove image"
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center pointer-events-none">
                          <svg
                            className="w-7 h-7 text-[#868686] mb-1.5 group-hover:text-[#50AB3D] transition-colors"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.5"
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <p className="text-xs font-medium text-[#1F2241]">
                            Click to upload or drag & drop
                          </p>
                          <p className="text-[10px] text-[#868686] mt-0.5 text-center">
                            {QUOTATION_PRODUCT_IMAGE_LABEL}
                            <br />
                            Not supported: HEIC, AVIF, SVG
                          </p>
                        </div>
                      )}
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={QUOTATION_PRODUCT_IMAGE_ACCEPT}
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                ) : (
                  /* Web URL Input */
                  <div className="flex flex-col gap-3">
                    <Input
                      type="url"
                      value={form.image_url}
                      onChange={(e) => {
                        const val = e.target.value;
                        setField('image_url', val);
                        setImageFile(null);
                        setImageRemoved(false);
                        setImagePreview(val);
                      }}
                      placeholder="https://example.com/product-image.jpg"
                      className="text-xs"
                      error={Boolean(errors.image_url)}
                    />
                    {form.image_url && (
                      <div className="flex items-center gap-3 p-2 bg-[#FAFAFA] rounded-xl border border-[#EBF4EA]">
                        <img
                          src={form.image_url}
                          alt="URL Preview"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                          onLoad={(e) => {
                            e.target.style.display = 'block';
                          }}
                          className="w-12 h-12 rounded-lg object-contain bg-white border border-[#EBF4EA] p-0.5 shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-[#1F2241] truncate">
                            {form.image_url}
                          </p>
                          <p className="text-[10px] text-[#868686]">
                            Web Image Preview
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setField('image_url', '');
                            setImagePreview('');
                          }}
                          className="text-xs text-red-500 hover:text-red-600 font-medium px-2 py-1"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </FormField>
            </div>
          </div>

          {/* ── Section: Pricing & Inventory ── */}
          <div className="flex flex-col gap-4">
            <h4 className="text-xs font-bold uppercase tracking-wider text-[#50AB3D] border-b border-[#EBF4EA] pb-1.5">
              Pricing & Inventory
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                label="Price"
                htmlFor="drawer-product-price"
                required
                error={errors.price}
                labelClassName={FIELD_LABEL_CLASS}
              >
                <Input
                  id="drawer-product-price"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={form.price}
                  onChange={(e) => setField('price', e.target.value)}
                  placeholder="0.00"
                  error={Boolean(errors.price)}
                />
              </FormField>

              <FormField
                label="Min Order Qty (MOQ)"
                htmlFor="drawer-product-moq"
                error={errors.moq}
                labelClassName={FIELD_LABEL_CLASS}
              >
                <Input
                  id="drawer-product-moq"
                  type="number"
                  min="1"
                  step="1"
                  value={form.moq}
                  onChange={(e) => setField('moq', e.target.value)}
                  error={Boolean(errors.moq)}
                />
              </FormField>

              <FormField
                label="Tax Rate (%)"
                htmlFor="drawer-product-tax-rate"
                error={errors.tax_rate}
                labelClassName={FIELD_LABEL_CLASS}
                className="sm:col-span-2"
              >
                <div className="flex flex-col gap-2">
                  <Input
                    id="drawer-product-tax-rate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    inputMode="decimal"
                    value={form.tax_rate}
                    onChange={(e) => setField('tax_rate', e.target.value)}
                    placeholder="0.00"
                    error={Boolean(errors.tax_rate)}
                    className="max-w-[140px]"
                  />
                  <div className="flex flex-wrap gap-1.5">
                    {TAX_RATE_PRESETS.map((preset) => {
                      const active =
                        form.tax_rate === ''
                          ? preset === '0'
                          : Number(form.tax_rate) === Number(preset);
                      return (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => setField('tax_rate', preset)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${
                            active
                              ? 'bg-primary text-white border-primary'
                              : 'bg-white text-text-secondary border-border-default hover:border-primary/40'
                          }`}
                        >
                          {preset}%
                        </button>
                      );
                    })}
                  </div>
                </div>
              </FormField>
            </div>
          </div>

          {/* ── Section: Availability ── */}
          <div className="flex items-center justify-between gap-3 p-3.5 rounded-xl bg-[#F9FAFB] border border-[#EBF4EA]">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#1F2241]">
                Available in Quotation
              </p>
              <p className="text-xs text-[#868686] mt-0.5">
                Disable to hide this product from the WhatsApp quotation
                catalog.
              </p>
            </div>
            <Switch
              checked={form.is_available}
              onChange={(checked) => setField('is_available', checked)}
              disabled={isPending}
              aria-label="Product available"
            />
          </div>
        </form>
      )}
    </SideDrawer>
  );
}

export default QuotationProductDrawer;
